import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import UsersPage from './pages/UsersPage'
import StoragePage from './pages/StoragePage'
import LogsPage from './pages/LogsPage'
import LoadingSpinner from './components/LoadingSpinner'
import Layout from './components/Layout'

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingSpinner />
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingSpinner />
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <>{children}</>
}

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/storage" element={<StoragePage />} />
                <Route path="/logs" element={<LogsPage />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}

const App: React.FC = () => {
  const isDevelopmentOrStaging = import.meta.env.VITE_ENVIRONMENT !== 'production';
  const env = import.meta.env;

  return (
    <AuthProvider>
      <div className="min-h-screen bg-gray-50">
        {isDevelopmentOrStaging && (
          <div style={{
            backgroundColor: '#fffbe5',
            color: '#644d04',
            padding: '10px',
            textAlign: 'left',
            borderBottom: '1px solid #f0e1a1',
            fontFamily: 'monospace',
            fontSize: '12px',
            lineHeight: '1.6',
            position: 'sticky',
            top: 0,
            zIndex: 9999
          }}>
            <strong style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
              [DIAGNOSTIC INFO - {env.VITE_ENVIRONMENT?.toUpperCase()}]
            </strong>
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '2px 10px' }}>
              <span>API URL:</span>
              <strong>{env.VITE_API_BASE_URL || 'Not Set!'}</strong>
              <span>App Version:</span>
              <strong>v{env.VITE_APP_VERSION || 'N/A'}</strong>
              <span>Commit SHA:</span>
              <strong>{env.VITE_GIT_COMMIT_SHA || 'N/A'}</strong>
              <span>Build Timestamp:</span>
              <strong>{env.VITE_BUILD_TIMESTAMP ? new Date(env.VITE_BUILD_TIMESTAMP).toLocaleString() : 'N/A'}</strong>
            </div>
          </div>
        )}
        <AppRoutes />
      </div>
    </AuthProvider>
  )
}

export default App